import React, { useMemo } from "react";
import CurrencyFormat from "react-currency-format";
import TripExtrasStoreSheet from "./TripExtrasStoreSheet";
import {
  estimateExtrasFromCart,
  extrasCartSignature,
  extrasLinesSignature,
} from "./tripExtrasHelpers";

const CATEGORY_ICONS = {
  bundles: "mdi-gift-outline",
  materials: "mdi-tools",
  boxes: "mdi-package-variant",
};

const FALLBACK_FEATURES = [
  { id: "boxes", label: "Boxes", icon: "mdi-package-variant" },
  { id: "wrap", label: "Wrap", icon: "mdi-layers-outline" },
  { id: "tape", label: "Tape", icon: "mdi-marker" },
  { id: "kits", label: "Starter kits", icon: "mdi-gift-outline" },
];

const TripExtrasPanel = ({
  catalog,
  cart = {},
  onChangeQty,
  onOpenSheet,
  sheetOpen,
  onCloseSheet,
  loading = false,
  allInPriceCents = 0,
  extrasSubtotalCents = 0,
  extrasLines = [],
}) => {
  const features = useMemo(() => {
    const cats = catalog?.categories || [];
    if (cats.length) {
      return cats.map((cat) => ({
        id: cat.id,
        label: cat.label,
        icon: CATEGORY_ICONS[cat.id] || "mdi-package-variant",
      }));
    }
    return FALLBACK_FEATURES;
  }, [catalog]);

  const { totalCents: previewExtrasCents } = useMemo(
    () => estimateExtrasFromCart(catalog, cart),
    [catalog, cart],
  );

  const cartItemCount = Object.values(cart).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0,
  );

  const cartMatchesQuote =
    extrasCartSignature(cart) === extrasLinesSignature(extrasLines);
  const tripPriceCents = Math.max(0, allInPriceCents - extrasSubtotalCents);
  const displayExtrasCents =
    cartMatchesQuote && !loading ? extrasSubtotalCents : previewExtrasCents;
  const showPreviewHint = !cartMatchesQuote && !loading && cartItemCount > 0;

  return (
    <div
      className={"lp2-extras" + (loading ? " is-refreshing" : "")}
      id="lp2-trip-extras"
    >
      <div className="lp2-extras-head">
        <h4 className="lp2-extras-head-title">
          Do you want supplies for your move?
        </h4>
        <p className="lp2-extras-head-sub">
          Optional add-ons, delivered with your driver on collection day.
        </p>
      </div>

      <ul className="lp2-extras-features">
        {features.map((feature) => (
          <li key={feature.id}>
            <button
              type="button"
              className="lp2-extras-feature"
              onClick={onOpenSheet}
              aria-expanded={sheetOpen}
            >
              <span className="lp2-extras-feature-icon" aria-hidden="true">
                <i className={`mdi ${feature.icon}`} />
              </span>
              <span className="lp2-extras-feature-label">{feature.label}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="lp2-extras-foot">
        {cartItemCount > 0 ? (
          <button type="button" className="lp2-extras-selection" onClick={onOpenSheet}>
            {cartItemCount} item{cartItemCount === 1 ? "" : "s"} added
            {displayExtrasCents > 0 ? (
              <>
                {" · +"}
                <CurrencyFormat
                  value={displayExtrasCents / 100}
                  displayType="text"
                  thousandSeparator
                  prefix="R"
                  decimalScale={2}
                />
                {showPreviewHint ? " (preview)" : loading ? " (updating…)" : ""}
              </>
            ) : null}
          </button>
        ) : (
          <span className="lp2-extras-foot-spacer" />
        )}
        <button
          type="button"
          className="lp2-extras-shop-link"
          onClick={onOpenSheet}
          aria-expanded={sheetOpen}
        >
          Boxes, wraps, tapes, labels and more
          {catalog?.totalCount ? ` (${catalog.totalCount})` : ""}
          <i className="mdi mdi-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <TripExtrasStoreSheet
        open={sheetOpen}
        onClose={onCloseSheet}
        catalog={catalog}
        categories={catalog?.categories || []}
        totalCount={catalog?.totalCount || 0}
        cart={cart}
        onChangeQty={onChangeQty}
        quoteLoading={loading}
        tripPriceCents={tripPriceCents}
        confirmedExtrasCents={extrasSubtotalCents}
        confirmedAllInCents={allInPriceCents}
        confirmedExtrasLines={extrasLines}
      />
    </div>
  );
};

export default TripExtrasPanel;
